// ═══════════════════════════════════════════════════════════════════════════
//  RELEASE NOTES — every PWA deploy that changes what a user gets adds an
//  entry here. `npm run deploy` refuses to publish without one.
// ═══════════════════════════════════════════════════════════════════════════
//
// The first time someone opens the app after a deploy, the service worker
// swaps in the new build and reloads (see index.tsx), and the «تازه‌های همیان»
// sheet then shows them every entry newer than the last one they saw. So what
// is written here is literally what users read. It is also the version in the
// sidebar and the `app_version` property on every PostHog event.
//
// ── HOW TO SHIP A VERSION ──────────────────────────────────────────────────
//
//  1. Add ONE new entry at the TOP of RELEASES (newest first). Several merges
//     going out in one deploy share one entry, one line each.
//  2. Pick the version from the entry below it (the rules are enforced):
//
//       fix    1.4.2 → 1.4.3   only fixes / polish of things users already had
//       minor  1.4.2 → 1.5.0   anything a user can newly see or do. Required
//                              as soon as one change is `new`.
//       major  1.4.2 → 2.0.0   the whole experience changes (a redesign).
//                              Only when the owner says so.
//
//     Exactly one step: no 1.4.2 → 1.6.0, no 1.4.2 → 1.5.1.
//  3. Build, then `npm run deploy`. It checks that the build carries this
//     version and that it is newer than what is live whenever src/ changed.
//     `node scripts/releases.mjs` runs the same rules without deploying.
//
// ── HOW TO WRITE IT ────────────────────────────────────────────────────────
//
//  - Persian, to the member, about what THEY notice. Not the commit message:
//      ✗ «fix(وام): useWizardHistory تاریخچه را خطی نگه دارد»
//      ✓ «دکمهٔ عقب در فرم درخواست وام، دقیقاً یک قدم برمی‌گردد.»
//  - `title` is the one line that makes the update feel worth it.
//  - A change nobody can notice (refactor, telemetry, build) does not get a
//    line. If a whole deploy is like that, say what it does for them —
//    «بارگذاری سریع‌تر صفحه‌ها» — or don't ship it on its own.
//  - No `→` in the text: it renders backwards in RTL. Use «از … به …».
//  - Never change the version of an entry that has been deployed. Fixing a
//    typo in its text is fine.
//
// This file must stay self-contained (no imports): the deploy script loads it
// outside webpack.

export type ChangeKind = 'new' | 'better' | 'fix'

export interface Change {
    /**
     * new «قابلیت جدید» — members could not see or do it before
     * better «بهبود» — something they had works nicer
     * fix «رفع مشکل» — something they had was broken
     */
    kind: ChangeKind
    text: string
}

export interface Release {
    /** major.minor.patch — see the rules above */
    version: string
    /** Gregorian YYYY-MM-DD of the deploy; shown to users in Jalali */
    date: string
    title: string
    changes: Change[]
}

export const RELEASES: Release[] = [
    {
        version: '1.29.1',
        date: '2026-09-29',
        title: 'درخواست گزارش صندوق روشن‌تر شد',
        changes: [
            {
                kind: 'better',
                text: 'بعد از درخواست «گزارش کامل صندوق» می‌بینید که درخواستتان ثبت شد، گزارش به کدام ایمیل و حدوداً کی می‌رسد، و لازم نیست صفحه را باز نگه دارید.',
            },
            {
                kind: 'better',
                text: 'پیش از درخواست، تعداد گزارش‌های باقی‌ماندهٔ این دوره را می‌بینید؛ اگر تمام شده باشد همان‌جا گفته می‌شود، نه با پیغام «مشکلی پیش آمده».',
            },
        ],
    },
    {
        version: '1.29.0',
        date: '2026-09-29',
        title: 'پاک کردن چند پیام با هم',
        changes: [
            {
                kind: 'new',
                text: 'در «پیام‌ها» روی «انتخاب» بزنید، هر چند پیام را که خواستید تیک بزنید و یک‌جا حذف کنید. «انتخاب چند پیام» در منوی ⋮ هر پیام هم هست.',
            },
            {
                kind: 'new',
                text: 'با «همه» و بعد «انتخاب همهٔ پیام‌های این فهرست»، همهٔ پیام‌های فهرست یک‌جا پاک می‌شوند، حتی قدیمی‌ترهایی که روی صفحه نیامده‌اند.',
            },
            {
                kind: 'fix',
                text: 'عدد پیام‌های نخوانده روی «پیام‌ها» بعد از خواندن پیام‌ها کم می‌شود و دیگر برنمی‌گردد.',
            },
        ],
    },
    {
        version: '1.28.1',
        date: '2026-09-29',
        title: 'پشتیبان هوشمند می‌داند کجا با او حرف می‌زنید',
        changes: [
            {
                kind: 'better',
                text: 'پشتیبان هوشمند حالا می‌داند در ایتا، بله یا مرورگر هستید و راهنمایی‌اش را با همان‌جا جور می‌کند.',
            },
            {
                kind: 'fix',
                text: 'پشتیبان هوشمند کمتر با «نتوانستم جواب بدهم» تمام می‌شود و دیگر بی‌انتها منتظرتان نمی‌گذارد.',
            },
        ],
    },
    {
        version: '1.28.0',
        date: '2026-09-28',
        title: 'پشتیبانی صندوق: اعضا در بله، ایتا یا تلفنی با مدیر حرف می‌زنند',
        changes: [
            {
                kind: 'new',
                text: 'مدیر صندوق در «تنظیمات صندوق»، بخش «پشتیبانی صندوق»، حساب بله، حساب ایتا و شماره تلفن پشتیبانی را برای هر صندوق جداگانه می‌گذارد.',
            },
            {
                kind: 'new',
                text: 'اعضا در صفحهٔ صندوق روی «تماس با پشتیبانی صندوق» می‌زنند و با یک لمس در بله یا ایتا به مدیر پیام می‌دهند یا تماس می‌گیرند.',
            },
            {
                kind: 'better',
                text: 'صفحهٔ «تنظیمات صندوق» مرتب‌تر شد: قاعدهٔ سررسید و حق عضویت بالای صفحه آمد و زیر هر گزینه نوشته شده آنجا چه چیزی تنظیم می‌شود.',
            },
        ],
    },
    {
        version: '1.27.1',
        date: '2026-09-28',
        title: 'در گفت‌وگوی درخواست وام روشن است پیام برای کیست',
        changes: [
            {
                kind: 'fix',
                text: '«خطاب به» فقط کنار نام متقاضی می‌آید؛ مدیری که در پیام از او نام برده شده، «از … نام برد» نوشته می‌شود.',
            },
        ],
    },
    {
        version: '1.27.0',
        date: '2026-09-28',
        title: 'صندوق قرعه‌کشی به حساب خود مدیر وصل می‌شود',
        changes: [
            {
                kind: 'new',
                text: 'مدیر صندوق قرعه‌کشی از «اتصال حساب» در تنظیمات، حساب بانکی خودش را وصل می‌کند تا پرداخت‌های اعضا به آن واریز شود.',
            },
            {
                kind: 'better',
                text: 'صفحهٔ صندوق قرعه‌کشی نشان می‌دهد پول صندوق نزد همیان می‌ماند یا به حساب مدیر می‌رود.',
            },
            {
                kind: 'better',
                text: 'صندوقی که هنوز فعال نشده، روی صفحه‌اش همین را می‌گوید. دعوت اعضا تا فعال‌سازی نگه داشته می‌شود و بعد همه با هم فرستاده می‌شود.',
            },
        ],
    },
    {
        version: '1.26.1',
        date: '2026-09-27',
        title: 'کارمزد هر پرداخت، کنار مبلغش',
        changes: [
            {
                kind: 'better',
                text: 'کارمزد هر پرداخت زیر مبلغش می‌آید؛ در جزئیات تراکنش هم مبلغ، کارمزد و جمعِ کم‌شده از حسابتان در یک کادرند.',
            },
            {
                kind: 'better',
                text: 'توضیح جزئیات تراکنش کوتاه شد: یک جمله، و فقط وقتی چیزی را روشن می‌کند.',
            },
        ],
    },
    {
        version: '1.26.0',
        date: '2026-09-27',
        title: 'تراکنش‌هایتان را بر اساس صندوق، نوع و تاریخ پیدا کنید',
        changes: [
            {
                kind: 'new',
                text: 'در «تراکنش‌ها» دکمهٔ فیلتر آمده است: صندوق، نوع تراکنش و بازهٔ روزها روی تقویم شمسی.',
            },
            {
                kind: 'fix',
                text: 'جمع بالای «تراکنش‌ها» حالا فقط تراکنش‌هایی را حساب می‌کند که در فهرست می‌بینید.',
            },
            {
                kind: 'new',
                text: 'مدیر صندوق حالا می‌تواند اعلان تابلوی اعلانات را ویرایش کند. دیگر لازم نیست آن را حذف کند و دوباره بنویسد.',
            },
            {
                kind: 'new',
                text: 'در کارتابل وام با «@» از مدیر دیگری نام ببرید تا خبردار شود. در «پیام‌ها» هم دو دستهٔ تازهٔ «تابلو» و «مدیران» اضافه شده است.',
            },
            {
                kind: 'new',
                text: 'در کارنامهٔ هر عضو، مدیر تاریخچهٔ تعداد سهم را می‌بیند: چه کسی، کی و از چند سهم به چند سهم.',
            },
            {
                kind: 'fix',
                text: 'اگر در پنجرهٔ «تعداد سهم» چیزی ننویسید و تأیید بزنید، سهم عضو دیگر صفر نمی‌شود. عوض کردن نقش یا ضامن هم به تعداد سهم دست نمی‌زند.',
            },
        ],
    },
    {
        version: '1.25.0',
        date: '2026-09-26',
        title: 'امتیاز اعضا را از هر تاریخی که خواستید، همان لحظه ببینید',
        changes: [
            {
                kind: 'better',
                text: 'در اعطای وام و فهرست اعضا، «امتیاز از» را روی ابتدا، امسال، ۱۲ ماه یا روز دلخواه بزنید؛ رتبه‌ها همان لحظه دوباره حساب می‌شود.',
            },
            {
                kind: 'better',
                text: 'این فقط نمایش شماست و ذخیره نمی‌شود؛ بقیهٔ مدیران امتیاز را مثل قبل می‌بینند.',
            },
        ],
    },
    {
        version: '1.24.0',
        date: '2026-09-26',
        title: 'نقش تازهٔ «بانی» در صندوق قرض‌الحسنه',
        changes: [
            {
                kind: 'new',
                text: 'مدیر می‌تواند عضوی را «بانی» کند تا گزارش مالی را ببیند و پرداخت‌ها را ثبت کند، بی‌آنکه مدیر صندوق شود.',
            },
            {
                kind: 'new',
                text: 'در تنظیمات صندوق، بخش «دسترسی بانی» تعیین می‌کند بانی چه کارهایی بکند.',
            },
        ],
    },
    {
        version: '1.23.0',
        date: '2026-09-26',
        title: 'مدیر صندوق تعیین می‌کند امتیاز اعضا از چه تاریخی حساب شود',
        changes: [
            {
                kind: 'new',
                text: 'در اعطای وام و فهرست اعضا، زیر «محاسبهٔ امتیاز» تاریخی را انتخاب کنید تا امتیاز و ترتیب اعضا از همان روز حساب شود.',
            },
        ],
    },
    {
        version: '1.22.1',
        date: '2026-09-26',
        title: '«مدیریت اعلان‌ها» مثل قبل',
        changes: [
            {
                kind: 'fix',
                text: 'سوییچ «پیامک» زیر هر نوع اعلان برداشته شد؛ پیامک‌ها مثل قبل با همان تنظیم کلی پیامک می‌آیند.',
            },
        ],
    },
    {
        version: '1.22.0',
        date: '2026-09-26',
        title: 'اعطای وام در سه قدم، با اقساط دلخواه',
        changes: [
            {
                kind: 'new',
                text: 'در اعطای وام، اقساط را مساوی، صعودی، نزولی یا رند بچینید، یا مبلغ و تاریخ هر قسط را جدا عوض کنید.',
            },
            {
                kind: 'better',
                text: 'اعطای وام از پنج قدم به سه قدم رسید: مبلغ، اقساط، کارمزد و ضامن همه در یک صفحه‌اند و ضامن عضویت با یک لمس انتخاب می‌شود.',
            },
            {
                kind: 'fix',
                text: 'جدول اقساط دلخواه برای وامی که کارمزدش مبلغ ثابت است دیگر نادیده گرفته نمی‌شود.',
            },
        ],
    },
    {
        version: '1.21.1',
        date: '2026-09-24',
        title: 'صفحهٔ مدیریت اعلان‌ها مرتب‌تر شد',
        changes: [
            {
                kind: 'fix',
                text: 'در رایانه، پس‌زمینهٔ صفحه‌های حساب کاربری تا پایین صفحه ادامه دارد و وسط صفحه قطع نمی‌شود.',
            },
            {
                kind: 'better',
                text: 'بخش «چون ضامن هستید» به‌جای فهرست بلند اسم‌ها، در یک خط می‌گوید ضامن چند وام و چند نفر هستید.',
            },
        ],
    },
    {
        version: '1.21.0',
        date: '2026-09-24',
        title: 'اعلان‌های همیان: کامل‌تر در بله و ایتا، و قابل تنظیم برای هر صندوق',
        changes: [
            {
                kind: 'new',
                text: '«مدیریت اعلان‌ها»: برای هر صندوق جدا انتخاب کنید چه خبرهایی برسد، با نمونهٔ هر پیام و دکمهٔ «ارسال آزمایشی».',
            },
            {
                kind: 'new',
                text: 'دکمه‌های زیر پیام‌ها همان تراکنش، همان اعلان تابلو یا همان پیام درخواست وام را باز می‌کنند و روشن نشانش می‌دهند.',
            },
            {
                kind: 'new',
                text: 'مدیران از صفحهٔ وام یا کارنامهٔ عضو، با پیامی از پیش‌نوشته از ضامن‌ها پیگیری می‌کنند؛ ضامن هم از همان پیام می‌تواند به‌جای عضو پرداخت کند.',
            },
            {
                kind: 'new',
                text: 'لینک دعوت به بله و ایتا برای مدیران، همراه با اینکه چند نفر از لینک شما وارد شدند.',
            },
            {
                kind: 'better',
                text: 'اعلان‌های تابلوی صندوق حالا به همهٔ اعضا خبر داده می‌شود، و مدیر پیش از انتشار می‌بیند به چند نفر و از چه راهی می‌رسد.',
            },
            {
                kind: 'better',
                text: 'مدیران خبر واریز و برداشت‌های صندوق را می‌توانند هر بار یا یک‌جا ساعت ۲۱ بگیرند.',
            },
        ],
    },
    {
        version: '1.20.1',
        date: '2026-09-23',
        title: 'توضیح تراکنش‌ها: وقتی یک شارژ چند پرداخت را با هم پوشانده',
        changes: [
            {
                kind: 'fix',
                text: 'اگر با یک شارژ چند پرداخت را یک‌جا انجام داده‌اید، توضیح آن شارژ حالا می‌گوید چند ردیف بالای آن روی‌هم همین مبلغ‌اند.',
            },
            {
                kind: 'fix',
                text: 'توضیح هر پرداخت، شارژِ مربوط به آن را پایین‌تر در فهرست نشان می‌دهد؛ پیش‌از‌این جای آن را اشتباه می‌گفت.',
            },
            {
                kind: 'fix',
                text: 'وقتی شارژ بیشتر از پرداخت‌ها بوده، دیگر نمی‌گوید موجودی کیف پول دست‌نخورده مانده؛ می‌گوید باقی‌اش به کیف پول اضافه شد.',
            },
        ],
    },
    {
        version: '1.20.0',
        date: '2026-09-23',
        title: 'تراکنش‌ها: کیف پول روشن‌تر و توضیح هر تراکنش',
        changes: [
            {
                kind: 'new',
                text: 'هر تراکنش حالا بخش «این تراکنش چه بود؟» دارد؛ به زبان ساده می‌گوید «−۵,۰۰۰ تومان» یعنی چه، پول از کجا رفت و به کجا رسید.',
            },
            {
                kind: 'new',
                text: 'کیف پول دو نوع شد: «شارژ کیف پول» و «برداشت از کیف پول»؛ پیش‌از‌این هر دو یک‌جور دیده می‌شدند.',
            },
            {
                kind: 'new',
                text: 'پرداخت با درگاه دو ردیف ثبت می‌شود، شارژ کیف پول و خرجش؛ حالا این دو به هم وصل‌اند و کنار شارژ می‌نویسیم برای کدام صندوق بود.',
            },
            {
                kind: 'better',
                text: 'رنگ و جهت فلش‌ها با صفحهٔ صندوق یکی شد: دریافتی سبز و رو به پایین، پرداختی قرمز و رو به بالا، در هر دو صفحه یک‌شکل.',
            },
            {
                kind: 'better',
                text: 'زیر هر ردیف برچسبی می‌گوید پول از کجا آمده: «با درگاه بانکی»، «برداشت از کیف پول»، «پرداخت نقدی، ثبت مدیر» یا «به حساب بانکی شما».',
            },
            {
                kind: 'better',
                text: 'جمع هر دوره دیگر پول را دوبار نمی‌شمارد؛ شارژی که همان لحظه خرج شده، در «شارژ کیف پول» نمی‌آید.',
            },
        ],
    },
    {
        version: '1.19.0',
        date: '2026-09-23',
        title: 'پیام‌ها: شمارهٔ نخوانده‌ها، فیلتر و گفت‌وگوی وام',
        changes: [
            {
                kind: 'new',
                text: 'تعداد پیام‌های نخوانده روی «پیام‌ها» در نوار پایین دیده می‌شود و تا وقتی نخوانیدشان — روی همین دستگاه یا روی گوشی — می‌ماند.',
            },
            {
                kind: 'new',
                text: 'فیلتر پیام‌ها بر اساس زمان (یک روز، یک هفته، یک ماه) و نوع پیام (قسط، حق عضویت، وام، واریز و برداشت، قرعه‌کشی و …).',
            },
            {
                kind: 'new',
                text: 'تب «گفت‌وگوی وام»: پیام‌های شما و مدیر صندوق روی هر درخواست وام، جدا از بقیه؛ مواردی که منتظر پاسخ شماست مشخص است.',
            },
            {
                kind: 'new',
                text: 'اطلاعیه‌های همیان هم در «پیام‌ها» می‌ماند؛ پیش‌از‌این فقط اعلان می‌آمد و بعد از ان پیدا نمی‌شد.',
            },
            {
                kind: 'better',
                text: 'پیام خوانده‌شده تیک سبز می‌گیرد و نخوانده مربع زرد؛ اگر پیامی را روی گوشی خوانده باشید، اینجا هم سبز می‌شود.',
            },
            {
                kind: 'better',
                text: 'روی گوشی اندازهٔ نوشته‌ها متناسب‌تر شده و صفحهٔ پیام‌ها روان‌تر و خوش‌دست‌تر است.',
            },
        ],
    },
    {
        version: '1.18.0',
        date: '2026-09-22',
        title: 'تب «تراکنش‌ها»، تازه و کامل‌تر',
        changes: [
            {
                kind: 'new',
                text: 'انتخاب بازه در تب «تراکنش‌ها» (این ماه، ماه گذشته، سه ماه اخیر، امسال)، با جمع دریافتی، پرداختی و شارژ کیف پول همان بازه.',
            },
            {
                kind: 'better',
                text: 'تراکنش‌ها روزبه‌روز دسته شده‌اند، هر ردیف می‌گوید چه پرداختی بوده و در کدام صندوق، و با لمس آن جزئیاتش باز می‌شود.',
            },
        ],
    },
    {
        version: '1.17.2',
        date: '2026-09-22',
        title: 'افزودن عضو، بی‌دردسر',
        changes: [
            {
                kind: 'fix',
                text: 'افزودن عضو با انتخاب ضامن حالا درست کار می‌کند: ضامن با یک لمس انتخاب می‌شود و عضو به صندوق اضافه می‌شود.',
            },
            {
                kind: 'better',
                text: 'اگر افزودن عضو انجام نشود، دلیلش را روشن می‌بینید؛ مثلاً شمارهٔ نادرست یا عضو تکراری.',
            },
        ],
    },
    {
        version: '1.17.1',
        date: '2026-09-22',
        title: 'پیام‌های صندوق، یک‌راست به صندوق',
        changes: [
            {
                kind: 'fix',
                text: 'پیام ساخت یا ویرایش صندوق قرض‌الحسنه هم حالا نام صندوق را نشان می‌دهد و با لمس، صفحهٔ همان صندوق را باز می‌کند.',
            },
        ],
    },
    {
        version: '1.17.0',
        date: '2026-09-22',
        title: '«پیام‌ها»، تازه و کاربردی',
        changes: [
            {
                kind: 'new',
                text: 'با لمس هر پیام به همان‌جایی می‌روید که باید: یادآوری پرداخت یک‌راست به پرداختِ همان صندوق، و دعوت‌نامه به صفحهٔ دعوت‌ها.',
            },
            {
                kind: 'new',
                text: 'حذف پیام، از دکمهٔ سه‌نقطهٔ کنار هر پیام.',
            },
            {
                kind: 'better',
                text: 'ظاهر تازهٔ «پیام‌ها»: نام صندوق روی هر پیام، دسته‌بندی بر اساس روز و نشانِ پیام‌های تازه.',
            },
        ],
    },
    {
        version: '1.16.0',
        date: '2026-09-21',
        title: 'همهٔ تراکنش‌های یک عضو، یک‌جا',
        changes: [
            {
                kind: 'new',
                text: '«همهٔ تراکنش‌های این عضو» در کارنامه: تب «تراکنش‌ها»ی صندوق، فقط با تراکنش‌های همان عضو.',
            },
            {
                kind: 'better',
                text: 'ردیف‌های «آخرین تراکنش‌ها» در کارنامه حالا قابل لمس‌اند؛ در کارنامهٔ خودتان هم «همهٔ تراکنش‌های شما» هست.',
            },
        ],
    },
    {
        version: '1.15.1',
        date: '2026-09-21',
        title: 'برگشت به صندوق، همان‌جایی که بودید',
        changes: [
            {
                kind: 'fix',
                text: 'در صفحهٔ صندوق، اگر از تب «وام‌ها» وامی را باز کنید و برگردید، دوباره روی همان تب «وام‌ها» هستید، نه «اعضا».',
            },
            {
                kind: 'fix',
                text: 'برگشتن از صفحهٔ یک عضو هم شما را به همان‌جای لیست اعضا می‌برد، با همان جست‌وجویی که کرده بودید.',
            },
            {
                kind: 'fix',
                text: 'کادر جست‌وجوی اعضا دیگر بعد از جست‌وجو خودبه‌خود بسته نمی‌شود.',
            },
        ],
    },
    {
        version: '1.15.0',
        date: '2026-09-21',
        title: 'همیان روی لپ‌تاپ و تبلت، جادارتر',
        changes: [
            {
                kind: 'better',
                text: 'روی لپ‌تاپ و تبلت، صفحه‌های همیان دیگر در ستونی باریک وسط صفحه فشرده نمی‌شوند و جای بیشتری دارند. روی گوشی چیزی عوض نشده است.',
            },
            {
                kind: 'better',
                text: 'روی صفحهٔ بزرگ، نوار پایین (خانه، صندوق‌ها، تراکنش‌ها و پیام‌ها) جمع‌وجور و وسط صفحه می‌نشیند.',
            },
        ],
    },
    {
        version: '1.14.3',
        date: '2026-09-21',
        title: 'پرداخت از نسخهٔ وب ایتا و بله',
        changes: [
            {
                kind: 'fix',
                text: 'در نسخهٔ وب ایتا و بله، صفحهٔ بانک دیگر سفید نمی‌ماند: درگاه در صفحهٔ جدید مرورگر باز می‌شود و بعد از پرداخت به همیان برمی‌گردید.',
            },
        ],
    },
    {
        version: '1.14.2',
        date: '2026-09-20',
        title: 'دیگر خودبه‌خود رفرش نمی‌شود',
        changes: [
            {
                kind: 'fix',
                text: 'در آیفون، صفحهٔ همیان گاهی پشت سر هم از نو باز می‌شد و کار نیمه‌تمام از دست می‌رفت؛ حالا فقط وقتی نسخهٔ تازه‌ای باشد صفحه نو می‌شود.',
            },
        ],
    },
    {
        version: '1.14.1',
        date: '2026-09-20',
        title: 'نمودار کارنامه، خواناتر',
        changes: [
            {
                kind: 'fix',
                text: 'در صفحهٔ «کارنامه»، سال‌های زیر نمودار سرمایه به انگلیسی نوشته می‌شد؛ حالا مثل بقیهٔ عددهای همیان، فارسی است.',
            },
            {
                kind: 'better',
                text: 'توضیح کنار همان نمودار هم ساده‌تر شد: به‌جای «مساحت زیر نمودار»، می‌گوید هرچه پول بیشتری مدت بیشتری در صندوق بماند، امتیاز بالاتر می‌رود.',
            },
        ],
    },
    {
        version: '1.14.0',
        date: '2026-09-20',
        title: 'پیامک را هم خودتان خاموش می‌کنید',
        changes: [
            {
                kind: 'new',
                text: 'در صفحهٔ «اعلان‌ها» کلید «پیامک» اضافه شد: اگر خبرها را در بله یا ایتا می‌گیرید و پیامک برایتان اضافه است، همان‌جا خاموشش کنید.',
            },
            {
                kind: 'better',
                text: 'زیر هر کلید نوشته‌ایم همین حالا خبرها از کجا به شما می‌رسد؛ و اگر همه را خاموش کنید، می‌گوییم خبرها همچنان در خود همیان و در «پیام‌ها» هست.',
            },
        ],
    },
    {
        version: '1.13.2',
        date: '2026-09-20',
        title: 'دکمهٔ بازگشت، همیشه کار می‌کند',
        changes: [
            {
                kind: 'fix',
                text: 'وقتی از پیامک، اعلان یا لینک یک صفحه را مستقیم باز می‌کردید، فلش بازگشت گوشهٔ صفحه کار نمی‌کرد؛ حالا شما را به صفحهٔ اصلی همیان می‌برد.',
            },
        ],
    },
    {
        version: '1.13.1',
        date: '2026-09-20',
        title: 'خبر هر نسخه، فقط یک بار',
        changes: [
            {
                kind: 'fix',
                text: 'پیام «همیان تازه شد» دیگر با هر بار باز کردن همیان تکرار نمی‌شود؛ هر نسخه یک بار. هر وقت خواستید از منو «تازه‌ها» را ببینید.',
            },
        ],
    },
    {
        version: '1.13.0',
        date: '2026-09-19',
        title: 'اجازهٔ اعلان، همان بار اول',
        changes: [
            {
                kind: 'better',
                text: 'کارت «فعال‌سازی اعلان‌ها» از صفحهٔ اصلی برداشته شد؛ حالا همان بار اول که همیان را باز می‌کنید، مرورگر یک بار اجازه را می‌پرسد.',
            },
            {
                kind: 'new',
                text: 'در صفحهٔ «اعلان‌ها» بخش «دسترسی اعلان در مرورگر» اضافه شد: هر وقت خواستید همان‌جا اجازه را می‌دهید یا پس می‌گیرید.',
            },
        ],
    },
    {
        version: '1.12.0',
        date: '2026-09-17',
        title: 'ورود با یک لمس در ایتا و بله',
        changes: [
            {
                kind: 'new',
                text: 'اگر همیان را داخل ایتا یا بله باز می‌کنید، با یک دکمه وارد شوید؛ بدون وارد کردن شماره و بدون کد پیامکی.',
            },
            {
                kind: 'new',
                text: 'اعلان‌های همیان در ایتا و بله، مستقیم همان صفحه‌ای را باز می‌کنند که پیام درباره‌اش بود.',
            },
            {
                kind: 'new',
                text: 'مدیران در «اطلاع‌رسانی به اعضا» می‌بینند همیان به هر عضو چه پیامی داده و از چه راهی: پیامک، پیام‌رسان یا اعلان.',
            },
        ],
    },
    {
        version: '1.11.0',
        date: '2026-09-17',
        title: 'تراکنش‌ها را بر اساس نوع فیلتر کنید',
        changes: [
            {
                kind: 'new',
                text: 'در «تراکنش‌ها» دکمهٔ فیلتر آمد: فقط واریزها، فقط برداشت‌ها، یا یک نوع مثل «پرداخت قسط» و «اعطای وام» را ببینید. تعداد نتیجه هم نوشته می‌شود.',
            },
            {
                kind: 'fix',
                text: 'جستجو با شماره همراه همان‌طور که می‌نویسید کار می‌کند: با صفر اول، بدون آن، یا با ارقام فارسی. در «اعضا» و «وام‌ها» هم همین‌طور.',
            },
            {
                kind: 'better',
                text: 'کادر جستجوی تراکنش‌ها یادآوری می‌کند که با #هشتگ هم می‌شود جستجو کرد.',
            },
        ],
    },
    {
        version: '1.10.0',
        date: '2026-09-17',
        title: 'از «ضمانت‌های من» مستقیم به اقساط همان وام',
        changes: [
            {
                kind: 'new',
                text: 'در صفحهٔ «ضمانت‌های من» با زدن روی هر وام، برنامهٔ اقساطش را می‌بینید و می‌توانید قسط‌های عقب‌افتاده را همان‌جا پرداخت کنید.',
            },
            {
                kind: 'fix',
                text: 'صفحهٔ «ضمانت‌های من» در مرورگر کامپیوتر دیگر تمام‌عرض نمی‌شود و مثل بقیهٔ صفحه‌ها می‌ماند.',
            },
        ],
    },
    {
        version: '1.9.0',
        date: '2026-09-17',
        title: '«پیگیری واریز» به جزئیات تراکنش آمد',
        changes: [
            {
                kind: 'new',
                text: 'در جزئیات هر وام یا برداشتی که همیان به حساب بانکی واریز کرده، «پیگیری واریز» هست: مدیر و گیرندهٔ پول می‌بینند پول کجاست، حتی بعد از واریز.',
            },
            {
                kind: 'better',
                text: 'جزئیات تراکنش هم‌شکل فهرست شد: مبلغ و وضعیت بالا، و بقیهٔ اطلاعات مرتب زیر آن. نام مدیری که تراکنش را ثبت کرده هم نوشته می‌شود.',
            },
            {
                kind: 'fix',
                text: 'دکمهٔ «اطلاعات وام» در جزئیات تراکنش حالا صفحهٔ همان وام را باز می‌کند.',
            },
            {
                kind: 'fix',
                text: 'زیر وام‌ها و قسط‌های ثبتی دیگر به‌اشتباه «مانده صندوق پس از تراکنش: ۰ تومان» نوشته نمی‌شود.',
            },
        ],
    },
    {
        version: '1.8.0',
        date: '2026-09-17',
        title: 'همهٔ تراکنش‌های صندوق، مرتب و قابل جستجو',
        changes: [
            {
                kind: 'new',
                text: 'حالا همهٔ تراکنش‌های صندوق را می‌بینید: با پایین رفتن در فهرست، قدیمی‌ترها هم می‌آیند. پیش از این فقط ۲۰ تای آخر دیده می‌شد.',
            },
            {
                kind: 'new',
                text: 'تراکنش‌ها را با نام عضو، شماره همراه یا بخشی از توضیحات جستجو کنید.',
            },
            {
                kind: 'better',
                text: 'تراکنش‌ها روزبه‌روز دسته‌بندی شده‌اند و هر ردیف جمع‌وجورتر و خواناتر است؛ برای دیدن جزئیات، روی خود ردیف بزنید.',
            },
            {
                kind: 'better',
                text: 'تب‌های «اعضا»، «تراکنش‌ها» و «وام‌ها» هنگام پایین رفتن در فهرست، بالای صفحه می‌مانند.',
            },
            {
                kind: 'fix',
                text: 'تراکنشی که مدیر دستی ثبت کرده، چه واریز باشد چه برداشت، همه‌جا با یک عنوان نشان داده می‌شود: «ثبت‌شده توسط مدیر».',
            },
        ],
    },
    {
        version: '1.7.1',
        date: '2026-09-17',
        title: 'کارنامهٔ متقاضی دقیق‌تر شد',
        changes: [
            {
                kind: 'fix',
                text: 'در خلاصهٔ کارنامهٔ متقاضی وام، عضوی که قسط‌هایش را با تأخیر داده دیگر سبز نشان داده نمی‌شود.',
            },
        ],
    },
    {
        version: '1.7.0',
        date: '2026-09-17',
        title: 'درخواست وام: پیگیری واریز و کارنامهٔ متقاضی در یک صفحه',
        changes: [
            {
                kind: 'new',
                text: 'اگر وام از موجودی نزد همیان پرداخت شده باشد، زیر «درخواست پذیرفته شد» لینک پیگیری واریز آمده و وضعیت واریز را همان‌جا می‌بینید.',
            },
            {
                kind: 'new',
                text: 'مدیران خلاصهٔ کارنامهٔ متقاضی را در صفحهٔ درخواست وام می‌بینند: امتیاز، سرمایه، مانده وام، معوق و نظم بازپرداخت، با یک لمس تا کارنامهٔ کامل.',
            },
            {
                kind: 'new',
                text: 'مدیران روی درخواست وام یادداشتی فقط برای مدیران می‌گذارند که عضو نمی‌بیند و پیامکی هم برایش نمی‌رود.',
            },
        ],
    },
    {
        version: '1.6.1',
        date: '2026-09-17',
        title: 'دکمهٔ پرداخت دیگر درخواست تکراری به بانک نمی‌فرستد',
        changes: [
            {
                kind: 'fix',
                text: 'بعد از زدن «پرداخت»، دکمه تا باز شدن صفحهٔ بانک قفل می‌ماند؛ زدن دوباره‌اش دیگر چند درخواست به درگاه نمی‌فرستد.',
            },
            {
                kind: 'fix',
                text: 'اگر برداشت وجه یا اعطای وام انجام نشود، دلیلش را می‌بینید؛ مثلاً «موجودی نزد همیان صندوق کافی نیست».',
            },
            {
                kind: 'better',
                text: 'صفحهٔ «جزئیات اطلاع‌رسانی» مرتب‌تر شد: تاریخ و ساعت به وقت ایران، و هزینهٔ بله فقط وقتی پیامی از بله رفته باشد.',
            },
        ],
    },
    {
        version: '1.6.0',
        date: '2026-09-16',
        title: 'اعلان نمی‌رسد؟ همین‌جا امتحانش کنید',
        changes: [
            {
                kind: 'new',
                text: '«ارسال اعلان آزمایشی» در صفحهٔ اعلان‌ها: اگر اعلان نرسید، می‌گوید کدام تنظیم گوشی یا کامپیوتر را درست کنید.',
            },
            {
                kind: 'fix',
                text: 'روشن کردن اعلان در بار اول دیگر بی‌صدا گیر نمی‌کند؛ نشان می‌دهد منتظر چیست.',
            },
        ],
    },
    {
        version: '1.5.0',
        date: '2026-09-16',
        title: 'صندوقی که بیرون از همیان دارید را بیاورید',
        changes: [
            {
                kind: 'new',
                text: 'دکمهٔ + در صفحهٔ صندوق‌ها حالا دو گزینه دارد: «ایجاد صندوق جدید» و «انتقال صندوق به همیان».',
            },
            {
                kind: 'new',
                text: 'اگر صندوقی از قبل دارید، بعد از تنظیمات صندوق مستقیم به ثبت سوابق مدیر می‌روید.',
            },
        ],
    },
    {
        version: '1.4.0',
        date: '2026-09-16',
        title: 'یادآوری‌ها و درخواست‌ها، با اعلان در همیان وب',
        changes: [
            {
                kind: 'new',
                text: 'با روشن کردن اعلان‌ها، سررسید حق عضویت و قسط، درخواستی که منتظر تأیید شماست و رسید پرداخت‌ها در مرورگر هم به شما خبر داده می‌شود.',
            },
            {
                kind: 'new',
                text: 'هفته‌ای یک بار، فقط اگر کاری منتظر شماست، یک یادآوری کوتاه می‌گیرید.',
            },
            {
                kind: 'new',
                text: 'از منوی «اعلان‌ها» انتخاب می‌کنید چه چیزهایی خبر داده شود. شب‌ها فقط رسید پرداخت‌ها، آن هم بی‌صدا، می‌رسد.',
            },
        ],
    },
    {
        version: '1.3.0',
        date: '2026-09-15',
        title: 'هدف صندوق را از همان اول بگویید',
        changes: [
            {
                kind: 'new',
                text: 'موقع ساختن صندوق قرض‌الحسنه یا قرعه‌کشی، هدف صندوق را انتخاب می‌کنید و اگر خواستید توضیح کوتاهی هم می‌نویسید.',
            },
        ],
    },
    {
        version: '1.2.1',
        date: '2026-09-15',
        title: 'اعطای وام نشان می‌دهد به کدام درخواست وصل می‌شود',
        changes: [
            {
                kind: 'better',
                text: 'در «اعطای وام جدید»، اگر عضو درخواست وام داشته باشد، مدیر از قدم اول می‌بیند که وام به همان درخواست وصل می‌شود.',
            },
            {
                kind: 'better',
                text: 'در فهرست گیرندگان وام، مانده وام هر عضو با همین نام نوشته می‌شود.',
            },
        ],
    },
    {
        version: '1.2.0',
        date: '2026-09-15',
        title: 'وام‌ها و درخواست‌ها به ترتیبی که مدیر می‌خواهد',
        changes: [
            {
                kind: 'new',
                text: 'مدیر، وام‌های صندوق را بر اساس قدیمی‌ترین قسط معوق، مبلغ معوق، مانده وام یا تاریخ وام مرتب می‌کند.',
            },
            {
                kind: 'new',
                text: 'در فهرست وام‌ها می‌شود فقط وام‌های معوق، جاری یا تسویه‌شده را دید.',
            },
            {
                kind: 'new',
                text: 'درخواست‌های وام به ترتیب قدیمی‌ترین، فوریت همراه با مدت انتظار، یا بیشترین موافقت مدیران مرتب می‌شوند.',
            },
            {
                kind: 'fix',
                text: 'درخواست‌های پذیرفته‌شده و ردشده از تازه‌ترین تصمیم شروع می‌شوند، نه از قدیمی‌ترین.',
            },
        ],
    },
    {
        version: '1.1.0',
        date: '2026-09-13',
        title: 'درخواست وام از نو، و از این به بعد خبر هر نسخهٔ تازه',
        changes: [
            {
                kind: 'new',
                text: 'هر بار همیان به‌روز شود، همین‌جا می‌گوییم چه چیزی تازه شده است.',
            },
            {
                kind: 'new',
                text: 'درخواست وام قدم‌به‌قدم ثبت می‌شود و مدیر هم می‌تواند برای عضو درخواست ثبت کند.',
            },
            {
                kind: 'new',
                text: 'هر درخواست وام صفحهٔ گفت‌وگوی خودش را دارد و مدیران نظرشان را همان‌جا ثبت می‌کنند.',
            },
            {
                kind: 'new',
                text: 'مدیر صندوق انتخاب می‌کند گزارش وام‌ها هفتگی برسد یا ماهانه.',
            },
            {
                kind: 'better',
                text: 'پشتیبان هوشمند جوابش را همان لحظه که می‌نویسد نشان می‌دهد.',
            },
            {
                kind: 'better',
                text: 'نام صندوق‌ها در فهرست، کامل و در دو خط خوانده می‌شود.',
            },
            {
                kind: 'fix',
                text: 'بعد از تمام‌شدن یک کار، دکمهٔ «عقب» دیگر شما را به همان فرم برنمی‌گرداند.',
            },
        ],
    },
]

export const APP_VERSION = RELEASES[0].version
